import React, { useState } from "react";
import { HiChevronDown } from "react-icons/hi2";
import { HealthLog } from "../../types/post";
import {
  useLogCardActions,
  LogCardActionsProps,
} from "../../hooks/useLogCardActions";
import LogCard from "../shared/LogCard";
import { LogCardNote } from "../shared/LogCardContent";
import SleepTimeline from "../common/SleepTimeline";
import { formatSleepDuration } from "../../utils";

interface HealthLogCardProps
  extends LogCardActionsProps<HealthLog & { id: string }> {
  log: HealthLog & { id: string };
  dateID: string;
}

const HealthLogCard: React.FC<HealthLogCardProps> = ({
  log,
  dateID,
  onDelete,
  onEdit,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const { handleDelete, handleEdit } = useLogCardActions(log, {
    onDelete,
    onEdit,
  });

  const handleCardClick = () => {
    setIsExpanded((prev) => !prev);
  };

  return (
    <div onClick={handleCardClick} className="cursor-pointer">
      <LogCard
        className={isExpanded ? "bg-base-100" : "bg-purple-500/10"}
        onDelete={onDelete ? handleDelete : undefined}
        onEdit={onEdit ? handleEdit : undefined}
      >
        {/* Header */}
        <div className="flex items-center gap-2 pr-8">
          <span className="text-[11px] font-medium tracking-wide uppercase text-base-content/70 font-geist">
            Health & Sleep
          </span>
          <HiChevronDown
            className={`w-3 h-3 text-base-content/30 transition-transform duration-200 ml-auto ${
              isExpanded ? "rotate-180" : ""
            }`}
          />
        </div>

        {/* Key metrics inline */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5">
          {log.sleepDuration && (
            <span className="text-xs text-base-content/70 font-geist">
              Sleep <strong className="text-base-content">{formatSleepDuration(log.sleepDuration)}</strong>
            </span>
          )}
          {log.rhr && (
            <span className="text-xs text-base-content/70 font-geist">
              RHR <strong className="text-base-content">{log.rhr}</strong>
            </span>
          )}
          {log.hrv && (
            <span className="text-xs text-base-content/70 font-geist">
              HRV <strong className="text-base-content">{log.hrv}</strong>
            </span>
          )}
          {log.respRate && (
            <span className="text-xs text-base-content/70 font-geist">
              Resp <strong className="text-base-content">{log.respRate}</strong>
            </span>
          )}
        </div>

        {isExpanded && (
          <div className="mt-3">
            {log.notes && <LogCardNote text={log.notes} />}
            <SleepTimeline
              dateID={dateID}
              className="mt-3 pt-3 border-t border-base-200"
              sleepTimeline={log.sleepTimeline}
              currentDay={log}
            />
          </div>
        )}
      </LogCard>
    </div>
  );
};

export default HealthLogCard;
